import { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.js";

const PDFJS_VERSION = "3.11.174";
const WORKER_CDN = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
const PDF_TO_IMAGE_SCALE = 2.5;

if (typeof pdfjsWorker !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}

function isPdfUrl(url) {
  if (!url || typeof url !== "string") return false;
  return (
    url.toLowerCase().endsWith(".pdf") ||
    url.toLowerCase().includes(".pdf?")
  );
}

async function fetchPdfAsArrayBuffer(url) {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
  return res.arrayBuffer();
}

async function pdfFirstPageToDataUrl(pdfLib, source, scale = PDF_TO_IMAGE_SCALE) {
  if (typeof source === "string" && source.startsWith("blob:")) {
    throw new Error("Blob URLs are not supported in this environment");
  }
  const docArg = typeof source === "string" ? source : { data: source };
  const loadingTask = pdfLib.getDocument(docArg);
  const pdfDoc = await loadingTask.promise;
  const page = await pdfDoc.getPage(1);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL("image/png", 1.0);
}

/**
 * Custom hook: given a URL (image or PDF), returns an image URL suitable for display.
 * - If the URL is a PDF, fetches it and converts the first page to a data URL (image).
 * - If the URL is not a PDF, returns it as-is.
 * @param {string | null} url - Image or PDF URL (e.g. from API)
 * @returns {{ imageUrl: string | null, loading: boolean, error: string | null }}
 */
export function usePdfToImageUrl(url) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || url === "") {
      setImageUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (!isPdfUrl(url)) {
      setImageUrl(url);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setImageUrl(null);

    (async () => {
      try {
        const pdfData = await fetchPdfAsArrayBuffer(url);
        if (cancelled) return;
        let dataUrl;
        try {
          dataUrl = await pdfFirstPageToDataUrl(pdfjsLib, pdfData);
        } catch (e1) {
          if (typeof window !== "undefined" && !window.electronAPI) {
            try {
              pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_CDN;
              dataUrl = await pdfFirstPageToDataUrl(pdfjsLib, pdfData);
            } catch (e2) {
              throw e1;
            }
          } else {
            throw e1;
          }
        }
        if (cancelled) return;
        setImageUrl(dataUrl || null);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load PDF");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { imageUrl, loading, error };
}
